<template>
  <div class="container-page">
    <h2 class="title title--h2">Запись перечня односторонних мер нетарифного регулирования</h2>
    <div v-if="isAccessAllowed">
      <div class="form-group required">
        <ComponentInput
          label="Наименование нетарифной меры"
          :select2Settings="select2Settings"
          :select2Options="measures"
          v-model="card.measureId"
          required
          type="select2"
        />
      </div>
      <div class="row">
        <div class="form-group required col-6">
          <ComponentInput
            label="Тип торговой операции"
            :select2Settings="select2Settings"
            :select2Options="tradingOperations"
            v-model="card.tradingOperationId"
            required
            type="select2"
          />
        </div>
        <div class="form-group required col-6">
          <ComponentInput
            label="Страна"
            :select2Settings="select2Settings"
            :select2Options="countries"
            v-model="card.countryId"
            required
            type="select2"
          />
        </div>
      </div>
      <div class="row">
        <div class="form-group required col-6">
          <ComponentInput
            label="Страновой охват"
            :select2Settings="select2Settings"
            :select2Options="countryCoverages"
            v-model="card.countryCoverageId"
            required
            type="select2"
          />
        </div>
      </div>
      <div class="form-group disabled-select required">
        <ComponentInput
          label="Товар"
          placeholder="Введите название товара"
          v-model="card.tnvedAlias"
          :maxLength="225"
          required
        />
      </div>
      <div class="form-group" style="position: relative">
        <ComponentInput
          v-model="card.tnvedCodes"
          type="textarea"
          label="Код ТН ВЭД ЕАЭС"
          placeholder="Введите коды ТН ВЭД ЕАЭС, каждый с новой строки, или выберите в каталоге"
        />
        <i class="fas fa-search catalog-icon" title="Найти в каталоге" @click="openTnvedCatalog"></i>
      </div>
      <div class="form-group required">
        <ComponentInput
          label="Отрасль"
          :select2Settings="select2SettingsMulti"
          :select2Options="industries"
          v-model="card.unilateralNonTrIndustryIds"
          type="select2"
          multiple
          required
        />
      </div>
      <div class="row">
        <div class="form-group col-6">
          <div class="form-label">Дата начала действия</div>
          <DateComponent v-model="card.periodDateFrom" :clearable="true" />
        </div>
        <div class="form-group col-6">
          <div class="form-label">Дата окончания действия</div>
          <DateComponent v-model="card.periodDateTo" :clearable="true" />
        </div>
      </div>
      <div class="row">
        <div class="form-group col-12">
          <ComponentInput
            v-model="card.legalActs"
            type="textarea"
            label="Правовой акт"
            placeholder="Введите правовые акты, каждый с новой строки"
          />
        </div>
      </div>
      <div class="form-group">
        <ComponentInput v-model="card.comment" type="textarea" label="Комментарии" />
      </div>
      <div class="form-group">
        <CheckboxInput v-model="card.isActive" label="Действующие" />
      </div>
    </div>
    <div v-if="isAccessAllowed" class="mb-16">
      <ButtonComponent class="mr-8" mod="gradient-bg" buttonType="button" v-on:click="save">Сохранить</ButtonComponent>
      <ButtonComponent mod="border" buttonType="button" v-on:click="close">Отмена</ButtonComponent>
    </div>
  </div>
  <section v-if="isAccessAllowed">
    <ModalComponent v-model="groupModal" title="Выбор группы товаров" @close="closeGroupCatalog">
      <CatalogSelectorRedesigned
        v-bind:selectedIds="productIds"
        route="nsitnved"
        :multiple="false"
        :selectGroupsOnly="true"
        @select="updateGroup"
        @close="closeGroupCatalog"
      ></CatalogSelectorRedesigned>
    </ModalComponent>
    <ModalComponent v-model="tnvedModal" title="Выбор товара" @close="closeTnvedCatalog">
      <CatalogSelectorRedesigned
        v-bind:selectedIds="productIds"
        route="nsitnved"
        :multiple="true"
        :selectLeafsOnly="true"
        :returnCodes="true"
        @select="updateTnved"
        @close="closeTnvedCatalog"
      ></CatalogSelectorRedesigned>
    </ModalComponent>
  </section>
</template>

<script>
  import { useVuelidate } from '@vuelidate/core';

  import ComponentInput from '@/common/components/ComponentInput.vue';
  import DateComponent from '@/common/components/DateComponent/DateComponent.vue';
  import ButtonComponent from '@/common/components/redesigned/ButtonComponent.vue';
  import CheckboxInput from '@/common/components/redesigned/CheckboxInput.vue';
  import Constants from '@/common/constants';
  import security from '@/common/mixins/security';
  import Utils from '@/common/utils';

  import ModalComponent from '@/components/modals/ModalComponent.vue';

  import CountryApi from '@/modules/nsi/api/country';
  import NsiApi from '@/modules/nsi/api/nsi';
  import CatalogSelectorRedesigned from '@/modules/nsi/components/CatalogSelectorRedesigned.vue';

  import Api from '../api/unilateralnontr';

  const PERMISSIONS = {
    create: Constants.Permissions.MeasureUnilateralNonTrCreate,
    edit: Constants.Permissions.MeasureUnilateralNonTrEdit,
  };

  export default {
    name: 'UnilateralNonTrCard',
    components: {
      CheckboxInput,
      ModalComponent,
      CatalogSelectorRedesigned,
      ButtonComponent,
      ComponentInput,
      DateComponent,
    },
    mixins: [security],
    data() {
      return {
        id: this.$route.params.id,
        isSecond: this.$route.params.isSecond == 'true',
        card: {
          id: 0,
          unilateralNonTrIndustryIds: [],
          tnvedId: null,
        },
        measures: [],
        countryCoverages: [],
        tradingOperations: [],
        countries: [],
        groupModal: false,
        tnvedModal: false,
        productIds: [],
        industries: [],
        select2Settings: Constants.select2Settings,
        select2SettingsMulti: Constants.select2SettingsMultiple,
        defaultRequest: {
          pageNumber: 1,
          pageSize: 0,
          isActive: true,
        },
      };
    },
    created() {
      const accessPermission = Number(this.id) > 0 ? PERMISSIONS.edit : PERMISSIONS.create;
      this.checkPermissionAndRedirect(accessPermission).then((isAllowed) => {
        if (!isAllowed) return;
        this.loadDictionaries();
        this.loadCard();
      });
    },
    methods: {
      loadCard() {
        if (this.id > 0) {
          Api.find(this.id).then((response) => {
            this.card = response.data;
            this.card.isSecond = this.isSecond;
          });
        } else this.card.isSecond = this.isSecond;
      },
      loadDictionaries() {
        Utils.searchNsi('NonTariffMeasures', {})
          .then((response) => {
            this.measures = response.data;
          })
          .catch((error) => {
            console.error(error.response.data);
          });
        NsiApi.search('nsitradingoperation', this.defaultRequest)
          .then((response) => {
            this.tradingOperations = response.data.items.map((x) => {
              return { text: x.name, id: x.id };
            });
          })
          .catch((error) => {
            console.error(error.response.data);
          });
        NsiApi.search('nsiindustry', this.defaultRequest)
          .then((response) => {
            this.industries = response.data.items.map((x) => {
              return { text: x.name, id: x.id };
            });
          })
          .catch((error) => {
            console.error(error.response.data);
          });
        Utils.loadSelectOptions('countryCoverages', this.countryCoverages, true);
        CountryApi.search(this.defaultRequest)
          .then((response) => {
            this.countries = response.data.items.map((x) => {
              return { text: x.nameFull || x.name, id: x.id };
            });
          })
          .catch((error) => {
            console.error(error.response.data);
          });
      },
      save() {
        this.v$.$touch();
        if (this.v$.$invalid) {
          Constants.alert.fire('Ошибка', 'Проверьте правильность заполнения полей', 'error');
          return;
        }
        Api.update(this.card)
          .then(() => {
            Constants.alert.fire('Сохранение', Constants.commonSuccess, 'success');
            if (this.card.id) {
              this.$router.push({ name: 'UnilateralNonTrCard', params: { id: this.card.id, action: 'edit' } });
            } else {
              this.$router.push({ name: 'UnilateralNonTrRegistry', params: { isSecond: this.isSecond } });
            }
          })
          .catch(() => {
            Constants.alert.fire('Сохранение', Constants.commonError, 'error');
          });
      },
      updateGroup(selectedIds) {
        this.card.tnvedGroupId = selectedIds[0];
        this.closeGroupCatalog();
      },
      closeGroupCatalog() {
        this.groupModal = false;
      },
      openTnvedCatalog() {
        this.productIds = [];
        this.tnvedModal = true;
      },
      updateTnved(selectedIds) {
        if (!this.card.tnvedCodes) {
          this.card.tnvedCodes = '';
        }
        this.card.tnvedCodes = this.card.tnvedCodes + (this.card.tnvedCodes ? '\n' : '') + selectedIds.join('\n');
        this.closeTnvedCatalog();
      },
      closeTnvedCatalog() {
        this.tnvedModal = false;
      },
      close() {
        if (this.isSecond) this.$router.push({ name: 'UnilateralNonTrRegistryOdnostoron' });
        else this.$router.push({ name: 'UnilateralNonTrRegistry' });
      },
    },
    setup: () => ({ v$: useVuelidate() }),
    validations() {
      return {
        card: {},
      };
    },
  };
</script>

<style lang="scss" scoped>
  @import '@/assets/theme/default/title.scss';
  .col-tnved {
    width: 25%;
  }

  .col-rate {
    width: 10%;
  }

  .stockTable {
    .catalog-icon {
      top: 18px;
    }
  }
</style>
